import React, { useRef, useState } from 'react';

import { useQuery } from '@apollo/react-hooks';
import { Box, Snackbar, TextField } from '@material-ui/core';
import MuiAlert from '@material-ui/lab/Alert';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { CustomButton } from '../../../../components/UI/CustomButton';
import { GET_TEAM_INVITE_LINK } from '../../../../graphql/user';
import { JOIN_TEAM_LINK } from '../../../../routes';
import { GetTeamInviteLinkArgs, Query } from '../../../../types';
import { MY_ACCOUNT_LINKS } from '../../pages/const';
import { CardSection } from '../CardSection';
import CardTitleWithNav from '../CardTitleWithNav';
import InviteExistingLearner from '../InviteExistingLearner/InviteExistingLearner';
import InviteLearnerForm from '../InviteLearnerForm';
import useStyles from './styles';

const InviteLearnerView = () => {
  const { t } = useTranslation();
  const classes = useStyles();
  const params = useParams();
  const teamId = parseInt(params?.id);
  const inputRef = useRef<HTMLInputElement>(null);
  const [alert, setAlert] = useState<string>('');

  const { data } = useQuery<Pick<Query, 'getTeamInviteLink'>, GetTeamInviteLinkArgs>(
    GET_TEAM_INVITE_LINK,
    {
      variables: { team_id: teamId },
    },
  );

  const hash = data?.getTeamInviteLink?.hash || '';
  const location = window?.location?.origin;
  const inviteLink = `${location}${JOIN_TEAM_LINK}${hash}`;

  const copyToClipBoard = () => {
    if (!navigator.clipboard) {
      if (inputRef.current) {
        inputRef.current.select();
        window['clipboardData'].clearData();
        window['clipboardData'].setData('text/html', inviteLink);
        setAlert('alert.linkCopied');
      }
    }
    navigator.clipboard.writeText(inviteLink).then(() => setAlert('alert.linkCopied'));
  };

  const hideAlert = (event?: React.SyntheticEvent, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setAlert('');
  };

  return (
    <Box className={classes.contentWrap}>
      <Box>
        <CardTitleWithNav
          iconClass={classes.icon}
          containerClass={classes.containerWrap}
          wrapperClass={classes.titleWrap}
          title={t('page.account.inviteLearner')}
          goBackLink={`${MY_ACCOUNT_LINKS.MyTeams}/${teamId}`}
        />
        <CardSection
          classes={{ wrapper: classes.wrapper }}
          title={t('page.account.addAnExisting')}
          text={[
            t('page.account.inviteToExistingToTeam1'),
            t('page.account.inviteToExistingToTeam2'),
          ]}
        >
          <InviteExistingLearner
            teamId={teamId}
            onSuccess={() => setAlert(t('alert.learnerAddedToTeam'))}
          />
        </CardSection>
        <CardSection
          classes={{ wrapper: classes.wrapper }}
          title={t('page.account.inviteWithLink')}
          text={[t('page.account.inviteWithLinkInfo1'), t('page.account.inviteWithLinkInfo2')]}
        >
          <Box className={classes.fullWidthContainer}>
            <Box className={classes.fullWidthContainer}>
              <TextField
                name="hash"
                inputRef={inputRef}
                className={classes.fullWidthContainer}
                value={inviteLink}
                variant="outlined"
                inputProps={{ readOnly: true }}
              />
            </Box>
            <CustomButton color="secondary" onClick={copyToClipBoard} btnClass={classes.btn}>
              {t('button.copyUrl')}
            </CustomButton>
          </Box>
        </CardSection>
        <CardSection
          classes={{ wrapper: classes.wrapper }}
          title={t('page.account.inviteWithEmail')}
          text={[t('page.account.inviteWithEmailInfo1'), t('page.account.inviteWithEmailInfo2')]}
        >
          <InviteLearnerForm setAlert={setAlert} teamId={teamId} />
        </CardSection>
      </Box>
      <Snackbar open={!!alert} autoHideDuration={3000} onClose={hideAlert}>
        <MuiAlert elevation={6} variant="filled" onClose={hideAlert} severity="success">
          {t(alert)}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
};

export default InviteLearnerView;
